/**
 * Qwen Image family on fal.ai (Phase 1, U8): qwen-image, qwen-image-edit, qwen-image-2,
 * qwen-image-2-pro.
 *
 * Replaces: falai.ts async image arms `case 'qwen-image' / 'qwen-image-edit'` and
 * `case 'qwen-image-2' / 'qwen-image-2-pro'` (generateAsyncImageLegacy), the generic builder in
 * ProviderService.generateImageFalAI, and the FAL_IMAGE_MODELS / FAL_IMAGE_TO_IMAGE_MODELS rows.
 * Pricing is CreditService's generic tail (model_registry base x the legacy resolution ladder).
 *
 * PURE: `import type` + sibling modules only (purity.test).
 *
 * Legacy quirks reproduced on purpose (P1 is payload- and price-neutral):
 *   - qwen-image-edit's TEXT-to-image endpoint is the edit endpoint (fal-ai/qwen-image-edit-2509),
 *     whose schema REQUIRES image_urls: an image-less editor run submits without it (fal 422s).
 *   - The editor adds num_inference_steps / guidance_scale / use_turbo (qwen-image*) and
 *     enable_prompt_expansion (qwen-image-2*); headless never does (generic builder). These are
 *     editor-only `legacy.wire`, because a descriptor param's wire cannot differ per surface (R10).
 *   - use_turbo is sent to qwen-image-edit's endpoint, whose schema has no such field (dead key).
 *   - A maskUrl-only editor request picks the edit endpoint with NO image_urls: the edit port is
 *     therefore `min: 0` (a pre-reserve refusal would change a legacy success into a 4xx).
 *   - Headless sends image_urls whenever inputs exist, on whichever endpoint it picked.
 */
import type { ModelDescriptor, Param, Port, Sku } from '../types';
import { FAL_IMAGE_SIZE_PRESETS } from '../_shared';

/** The legacy nodes' reference-image port. `min: 0`: legacy submits an edit with no image (see header). */
const REFS_PORT: Port = { slot: 'refs', kind: 'image', min: 0, max: 10, handle: 'input-image' };
const REFS_WIRE = { field: 'image_urls', many: true, encode: 'image' } as const;
const EDIT_2509 = 'fal-ai/qwen-image-edit-2509';

const ASPECT: Param = {
  id: 'aspectRatio', type: 'string', default: '1:1', invalid: 'passthrough',
  wire: { kind: 'imageSize', field: 'image_size', presets: FAL_IMAGE_SIZE_PRESETS, fallback: 'square_hd' },
  ui: { label: 'Aspect ratio', tier: 'basic' },
};

/** Billing-only ladder axis (CreditService generic tail): the raw body/params resolution. */
const RESOLUTION: Param = { id: 'resolution', type: 'string', default: null, role: 'tier', invalid: 'passthrough', wire: false };

/** Sent when truthy, on both surfaces (editor `if (negativePrompt)`, headless spread). */
const NEGATIVE: Param = {
  id: 'negativePrompt', type: 'string', default: null, invalid: 'passthrough',
  wire: { field: 'negative_prompt' }, ui: { label: 'Negative prompt', tier: 'advanced' },
};

const OMIT_NEGATIVE = { field: 'negative_prompt', why: 'absent/blank -> omitted (legacy truthiness check); fal default " " / "" applies' };

const sku = (id: string, displayName: string, base: number, vendor: Sku['vendor'], marginException?: string): Sku => ({
  id,
  rate: { ladder: { base, by: 'resolution', curve: 'legacy-resolution-v1' } },
  vendor,
  ...(marginException ? { marginException } : {}),
  registry: { displayName, operationType: 'image', flatRate: false },
});

// ─── qwen-image / qwen-image-edit (Qwen-Image, 2509 edit) ─────────────────────

const qwenV1 = (o: {
  id: 'qwen-image' | 'qwen-image-edit'; name: string; t2i: string;
  quality: readonly string[]; steps: Readonly<Record<string, number>>; sku: Sku;
}): ModelDescriptor => ({
  id: o.id, provider: 'falai', face: 'image', displayName: o.name, ledgerName: o.name, status: 'live',
  prompt: { field: 'prompt', required: false },
  modes: {
    // With inputs both surfaces pick `edit`, so no refs reach t2i. qwen-image-edit's t2i endpoint IS
    // the 2509 edit endpoint (image_urls required), so it keeps the wire the schema demands.
    t2i: { endpoint: o.t2i, ports: [], media: o.t2i === EDIT_2509 ? { refs: REFS_WIRE } : {} },
    edit: { endpoint: EDIT_2509, ports: [REFS_PORT], media: { refs: REFS_WIRE } },
  },
  fixed: { num_images: 1, enable_safety_checker: false, output_format: 'png' },
  params: [
    ASPECT,
    // QUALITY_STEPS[model][quality] || QUALITY_STEPS[model].balanced: an unknown value steps like
    // 'balanced', which is what coercing to the default reproduces.
    { id: 'quality', type: 'enum', values: o.quality, default: 'balanced', invalid: { coerce: { kind: 'default' } }, wire: false,
      ui: { label: 'Quality', tier: 'basic' } },
    NEGATIVE,
    RESOLUTION,
  ],
  legacy: {
    wire: [
      { field: 'num_inference_steps', fromParam: 'quality', map: o.steps, surface: 'editor',
        why: 'editor QUALITY_STEPS (falai.ts:80-83); headless generic builder never sends it', retire: 'owner-approval' },
      { field: 'guidance_scale', value: 2.5, modes: ['t2i'], surface: 'editor',
        why: 'editor `useImageToImage ? 4 : 2.5`; headless never sends it', retire: 'owner-approval' },
      { field: 'guidance_scale', value: 4, modes: ['edit'], surface: 'editor',
        why: 'editor `useImageToImage ? 4 : 2.5`; headless never sends it', retire: 'owner-approval' },
      { field: 'use_turbo', fromParam: 'quality', map: { turbo: true }, surface: 'editor',
        why: o.id === 'qwen-image'
          ? "editor `quality === 'turbo'` (on the edit endpoint the key is absent from QwenImageEdit2509Input: dead there)"
          : "editor `quality === 'turbo'`; absent from QwenImageEdit2509Input (oracle 2026-09-19): dead key",
        retire: 'owner-approval' },
    ],
    omits: [OMIT_NEGATIVE],
    notes: o.id === 'qwen-image-edit'
      ? [
        't2i posts to fal-ai/qwen-image-edit-2509 without the required image_urls (legacy FAL_IMAGE_MODELS row); headless reaches it.',
        "The editor never does: the route middleware validateImageRequest (falai.ts:151-154, before the engine dispatch) answers 400 'imageUrl or imageUrls is required for Qwen Image Edit'. It is NOT reproduced here; C5 must keep it (or move it into the edit port's min) when the image arms are deleted.",
      ]
      : [],
  },
  pricing: { unit: 'generation', units: { fixed: 1 }, rounding: 'generic', policy: 'fal-image-35', skus: [o.sku] },
  schemaVerified: '2026-09-19',
});

export const QWEN_IMAGE = qwenV1({
  id: 'qwen-image', name: 'Qwen Image', t2i: 'fal-ai/qwen-image',
  quality: ['turbo', 'balanced', 'high'], steps: { turbo: 10, balanced: 30, high: 50 },
  sku: sku('qwen-image', 'Qwen Image', 9, {
    usd: 0.02, per: 'megapixel', field: 'pricingInfoOverride', source: 'fal-ai/qwen-image',
    text: 'Your  request will cost **$0.02** per megapixel.', verified: '2026-09-19',
  }, 'fal bills $0.02/MP rounded UP to the nearest MP; 9 = the 1 MP price at 450 cr/$ (policy gives 10). Kept (P1 price-neutral).'),
});

export const QWEN_IMAGE_EDIT = qwenV1({
  id: 'qwen-image-edit', name: 'Qwen Image Edit', t2i: 'fal-ai/qwen-image-edit-2509',
  quality: ['fast', 'balanced', 'high', 'turbo'], steps: { fast: 15, balanced: 30, high: 50, turbo: 30 },
  sku: sku('qwen-image-edit', 'Qwen Image Edit', 14, {
    usd: 0.03, per: 'megapixel', field: 'sidebar', source: 'fal-ai/qwen-image-edit-2509 (rendered "Your request will cost")',
    text: 'Your request will cost $0.03 per megapixel.', verified: '2026-09-19',
  }),
});

// ─── qwen-image-2 / qwen-image-2-pro ─────────────────────────────────────────

const qwenV2 = (o: { id: 'qwen-image-2' | 'qwen-image-2-pro'; name: string; slug: string; sku: Sku }): ModelDescriptor => ({
  id: o.id, provider: 'falai', face: 'image', displayName: o.name, ledgerName: o.name, status: 'live',
  prompt: { field: 'prompt', required: false },
  modes: {
    t2i: { endpoint: `${o.slug}/text-to-image`, ports: [], media: {} },   // with inputs both surfaces pick edit
    edit: { endpoint: `${o.slug}/edit`, ports: [REFS_PORT], media: { refs: REFS_WIRE } },
  },
  fixed: { num_images: 1, enable_safety_checker: false, output_format: 'png' },
  params: [
    ASPECT,
    { id: 'quality', type: 'string', default: 'balanced', invalid: 'passthrough', wire: false, ui: { label: 'Quality', tier: 'hidden' } },
    NEGATIVE,
    RESOLUTION,
  ],
  legacy: {
    wire: [
      { field: 'enable_prompt_expansion', value: true, surface: 'editor',
        why: 'editor constant (the node setting is ignored); headless omits it and fal defaults to true', retire: 'inert-cleanup' },
    ],
    omits: [OMIT_NEGATIVE],
  },
  pricing: { unit: 'generation', units: { fixed: 1 }, rounding: 'generic', policy: 'fal-image-35', skus: [o.sku] },
  schemaVerified: '2026-09-19',
});

export const QWEN_IMAGE_2 = qwenV2({
  id: 'qwen-image-2', name: 'Qwen Image 2', slug: 'fal-ai/qwen-image-2',
  sku: sku('qwen-image-2', 'Qwen Image 2', 16, {
    usd: 0.035, per: 'image', field: 'sidebar', source: 'fal-ai/qwen-image-2/text-to-image and /edit (rendered "Your request will cost")',
    text: 'Your request will cost $0.035 per image.', verified: '2026-09-19',
  }, '16 = ceil(0.035 x 450) (policy ceil(0.035 x 300/0.65) = 17). Kept (P1 price-neutral).'),
});

export const QWEN_IMAGE_2_PRO = qwenV2({
  id: 'qwen-image-2-pro', name: 'Qwen Image 2 Pro', slug: 'fal-ai/qwen-image-2/pro',
  sku: sku('qwen-image-2-pro', 'Qwen Image 2 Pro', 35, {
    usd: 0.075, per: 'image', field: 'sidebar', source: 'fal-ai/qwen-image-2/pro/text-to-image and /edit (rendered "Your request will cost")',
    text: 'Your request will cost $0.075 per image.', verified: '2026-09-19',
  }),
});

export const QWEN_MODELS: readonly ModelDescriptor[] = [QWEN_IMAGE, QWEN_IMAGE_EDIT, QWEN_IMAGE_2, QWEN_IMAGE_2_PRO];
